/**
 * PortfolioValue - Reusable component for displaying portfolio total value
 * Shows value with optional daily change and formatted currency
 */

import React from 'react';
import TrendIndicator from './TrendIndicator';

export interface PortfolioValueProps {
  value: number;
  currency?: string;
  change?: {
    value: number;
    percentage: number;
  };
  label?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showChange?: boolean;
  className?: string;
}

const PortfolioValue: React.FC<PortfolioValueProps> = ({
  value,
  currency = 'CHF',
  change,
  label = 'Total Portfolio Value',
  size = 'lg',
  showChange = true,
  className = ''
}) => {
  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'py-2 px-3',
      label: 'text-xs',
      value: 'text-lg',
      change: 'text-xs'
    },
    md: {
      container: 'py-3 px-4',
      label: 'text-sm',
      value: 'text-2xl',
      change: 'text-sm'
    },
    lg: {
      container: 'py-4 px-6',
      label: 'text-base',
      value: 'text-3xl',
      change: 'text-base'
    },
    xl: {
      container: 'py-6 px-8',
      label: 'text-lg',
      value: 'text-4xl',
      change: 'text-lg'
    }
  };
  
  const config = sizeConfig[size];
  
  // Format currency value
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);
  };
  
  // Format change value
  const formatChange = (val: number) => {
    const formatted = new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay: 'always'
    }).format(val);
    return formatted;
  };
  
  const isPositiveChange = change && change.value > 0;
  const changeColor = isPositiveChange ? 'text-green-600' : 'text-red-600';
  
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${config.container} ${className}`}>
      <div>
        <p className={`${config.label} text-gray-600 font-medium mb-2`}>
          {label}
        </p>
        
        <div className="flex items-baseline gap-4">
          <h2 className={`${config.value} font-bold text-gray-900`}>
            {formatCurrency(value)}
          </h2>
          
          {showChange && change && (
            <div className={`flex items-center gap-2 ${changeColor}`}>
              <span className={`${config.change} font-semibold`}>
                {formatChange(change.value)}
              </span>
              <TrendIndicator 
                value={change.percentage} 
                showIcon={true}
                showValue={true}
                iconSize={size === 'xl' ? 'lg' : size === 'lg' ? 'md' : 'sm'}
              />
            </div>
          )}
        </div>
        
        {showChange && change && (
          <p className="text-xs text-gray-500 mt-2">
            Daily Change
          </p>
        )}
      </div>
    </div>
  );
};

export default PortfolioValue;